/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Bonus } from '../models/Bonus';
import type { BonusCreateRequest } from '../models/BonusCreateRequest';
import type { BonusMessageTemplate } from '../models/BonusMessageTemplate';
import type { BonusMessageTemplateRequest } from '../models/BonusMessageTemplateRequest';
import type { PaginatedBonusList } from '../models/PaginatedBonusList';
import type { PaginatedBonusMessageTemplateList } from '../models/PaginatedBonusMessageTemplateList';
import type { PatchedBonusMessageTemplateRequest } from '../models/PatchedBonusMessageTemplateRequest';
import type { PatchedBonusRequest } from '../models/PatchedBonusRequest';
import type { RetroSpecialOccasionCountRequestRequest } from '../models/RetroSpecialOccasionCountRequestRequest';
import type { RetroSpecialOccasionCountResponse } from '../models/RetroSpecialOccasionCountResponse';
import type { TaxTreatmentEnum } from '../models/TaxTreatmentEnum';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class BonusService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Returns full bonus details for the managed companies
     * @returns PaginatedBonusList
     * @throws ApiError
     */
    public bonusList({
        company,
        limit,
        offset,
    }: {
        company?: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedBonusList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/bonus/',
            query: {
                'company': company,
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * Since almost all actions in the pxtra API must happen in the context of a specific company,
     * this mixin is provided to find out the targeted company and ensure the correct context.
     *
     * The target company is found in one of the following ways:
     * * If the current user is a company manager and provided the company param, this will determine
     * the target company. Lists require the param from managers acting through the dashboard
     * (platform token) and from managers without an employee record.
     * * If the current user is an employee, the target company is the employee's own company
     *
     *
     * After finding out the target company, the following measures are taken to ensure that only
     * the target company will be affected by any action:
     * * The viewset's queryset will be limited to objects associated with the target company
     * via the `filter_company_key`
     * * If the user is not associated with the target company, the permission will be denied
     * @returns Bonus
     * @throws ApiError
     */
    public bonusCreate({
        company,
        requestBody,
    }: {
        company: string,
        requestBody: BonusCreateRequest,
    }): CancelablePromise<Bonus> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/bonus/',
            query: {
                'company': company,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Since almost all actions in the pxtra API must happen in the context of a specific company,
     * this mixin is provided to find out the targeted company and ensure the correct context.
     *
     * The target company is found in one of the following ways:
     * * If the current user is a company manager and provided the company param, this will determine
     * the target company. Lists require the param from managers acting through the dashboard
     * (platform token) and from managers without an employee record.
     * * If the current user is an employee, the target company is the employee's own company
     *
     *
     * After finding out the target company, the following measures are taken to ensure that only
     * the target company will be affected by any action:
     * * The viewset's queryset will be limited to objects associated with the target company
     * via the `filter_company_key`
     * * If the user is not associated with the target company, the permission will be denied
     * @returns Bonus
     * @throws ApiError
     */
    public bonusRetrieve({
        id,
    }: {
        /**
         * A UUID string identifying this bonus.
         */
        id: string,
    }): CancelablePromise<Bonus> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/bonus/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Since almost all actions in the pxtra API must happen in the context of a specific company,
     * this mixin is provided to find out the targeted company and ensure the correct context.
     *
     * The target company is found in one of the following ways:
     * * If the current user is a company manager and provided the company param, this will determine
     * the target company. Lists require the param from managers acting through the dashboard
     * (platform token) and from managers without an employee record.
     * * If the current user is an employee, the target company is the employee's own company
     *
     *
     * After finding out the target company, the following measures are taken to ensure that only
     * the target company will be affected by any action:
     * * The viewset's queryset will be limited to objects associated with the target company
     * via the `filter_company_key`
     * * If the user is not associated with the target company, the permission will be denied
     * @returns Bonus
     * @throws ApiError
     */
    public bonusPartialUpdate({
        id,
        requestBody,
    }: {
        /**
         * A UUID string identifying this bonus.
         */
        id: string,
        requestBody?: PatchedBonusRequest,
    }): CancelablePromise<Bonus> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/bonus/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Cancels a bonus by setting its status to CANCELED
     * @returns void
     * @throws ApiError
     */
    public bonusDestroy({
        id,
    }: {
        /**
         * A UUID string identifying this bonus.
         */
        id: string,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/bonus/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Returns available tax treatments based on company's booked benefits
     * @returns any
     * @throws ApiError
     */
    public bonusAvailableTaxTreatmentsRetrieve({
        company,
    }: {
        /**
         * Company UUID to get available tax treatments for
         */
        company: string,
    }): CancelablePromise<{
        available_tax_treatments?: Array<TaxTreatmentEnum>;
    }> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/bonus/available-tax-treatments/',
            query: {
                'company': company,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns PaginatedBonusMessageTemplateList
     * @throws ApiError
     */
    public bonusMessageTemplatesList({
        company,
        limit,
        offset,
    }: {
        company?: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedBonusMessageTemplateList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/bonus/message-templates/',
            query: {
                'company': company,
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns BonusMessageTemplate
     * @throws ApiError
     */
    public bonusMessageTemplatesCreate({
        requestBody,
    }: {
        requestBody: BonusMessageTemplateRequest,
    }): CancelablePromise<BonusMessageTemplate> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/bonus/message-templates/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns BonusMessageTemplate
     * @throws ApiError
     */
    public bonusMessageTemplatesRetrieve({
        id,
    }: {
        /**
         * A UUID string identifying this bonus message template.
         */
        id: string,
    }): CancelablePromise<BonusMessageTemplate> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/bonus/message-templates/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns BonusMessageTemplate
     * @throws ApiError
     */
    public bonusMessageTemplatesUpdate({
        id,
        requestBody,
    }: {
        /**
         * A UUID string identifying this bonus message template.
         */
        id: string,
        requestBody: BonusMessageTemplateRequest,
    }): CancelablePromise<BonusMessageTemplate> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/bonus/message-templates/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns BonusMessageTemplate
     * @throws ApiError
     */
    public bonusMessageTemplatesPartialUpdate({
        id,
        requestBody,
    }: {
        /**
         * A UUID string identifying this bonus message template.
         */
        id: string,
        requestBody?: PatchedBonusMessageTemplateRequest,
    }): CancelablePromise<BonusMessageTemplate> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/bonus/message-templates/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns void
     * @throws ApiError
     */
    public bonusMessageTemplatesDestroy({
        id,
    }: {
        /**
         * A UUID string identifying this bonus message template.
         */
        id: string,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/bonus/message-templates/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Return the number of employees among the given IDs whose birthday or anniversary already occurred earlier in the current calendar month.
     * @returns RetroSpecialOccasionCountResponse
     * @throws ApiError
     */
    public bonusRetroSpecialOccasionCountCreate({
        requestBody,
    }: {
        requestBody: RetroSpecialOccasionCountRequestRequest,
    }): CancelablePromise<RetroSpecialOccasionCountResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/bonus/retro-special-occasion-count/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

}
